import { uploadProblem, type AttachmentSummary } from '@tradeoutlet/shared';
import { api } from './api';
import { measureImage } from '@/components/media/ImageGallery';

/**
 * Two-step upload: reserve + signed URL from the API, PUT bytes straight to Supabase Storage,
 * then confirm. The API never proxies file bytes and the browser never sees bucket credentials.
 */
export async function uploadFile(file: File, target: { workspace_id: string; conversation_id?: string; task_id?: string; purpose?: 'chat' | 'task' | 'kudos' | 'skill_evidence' | 'handover' | 'checklist' | 'incident' | 'announcement' | 'kb' | 'feedback' | 'hr_document' | 'shift_message' | 'policy'; caption?: string }): Promise<AttachmentSummary> {
  // t363u / t364u: only allowed types (photos, PDFs, Office, CSV / text, videos) up to 50 MB; say so before sending any bytes.
  const problem = uploadProblem(file);
  if (problem) throw new Error(problem);
  const dims = await measureImage(file);
  const res = await api.createUpload({ ...target, file_name: file.name, content_type: file.type || 'application/octet-stream', size_bytes: file.size, ...(dims ?? {}) });
  // Store it under the type the API settled on (e.g. text/csv, not Windows' "Excel" for a .csv); the API checks the bytes.
  const put = await fetch(res.upload_url, { method: 'PUT', headers: { 'content-type': res.attachment.content_type || file.type || 'application/octet-stream', 'x-upsert': 'false' }, body: file });
  if (!put.ok) throw new Error(`Upload failed (${put.status})`);
  return api.completeUpload(res.attachment.id);
}

export async function openAttachment(id: string, download = false) {
  const { url } = await api.attachmentUrl(id, download);
  window.open(url, '_blank', 'noopener');
}

/**
 * The X on an upload that hasn't been saved yet (forms, issue reports, handovers): ask the server to delete it so it
 * doesn't linger in storage. Best effort and silent; the server refuses files that are already saved somewhere, which
 * are cleaned up when the record itself is saved without them.
 */
export function discardUpload(id: string): void {
  void api.discardUpload(id).catch(() => undefined);
}
